/**
 * Pure helpers for mutating the lineage `map` graph (nodes / edges / mappings).
 *
 * No React / Redux imports — unit-tested in isolation, called by the reducer.
 * Every function is non-mutating: it returns new arrays/objects.
 * See plan §B2.2 (cascade + change partitioning), §B2.3 (ordering),
 * §B2.4 (group/ungroup math), §B2.9 (highlight ref-preservation).
 */

const GROUP_PADDING = 24;

/** children keyed by parentId. */
const childrenByParent = (nodes) => {
  const map = new Map();
  nodes.forEach((n) => {
    if (!n.parentId) return;
    if (!map.has(n.parentId)) map.set(n.parentId, []);
    map.get(n.parentId).push(n);
  });
  return map;
};

/** Transitive member ids of a group (children, grandchildren, …). */
export const getTransitiveMembers = (nodes, groupId) => {
  const byParent = childrenByParent(nodes);
  const acc = new Set();
  const collect = (id) => (byParent.get(id) || []).forEach((child) => {
    if (acc.has(child.id)) return;
    acc.add(child.id);
    collect(child.id);
  });
  collect(groupId);
  return acc;
};

/**
 * Collapse transform (display-only, system view). For each node returns its visible
 * representative — its OUTERMOST collapsed-group ancestor, or itself when none is
 * collapsed. `hidden` are nodes inside a collapsed group (not rendered); `collapsed`
 * are the group ids currently collapsed. map.nodes are never mutated: `data.collapsed`
 * is the only real state, everything else is derived so expansion is lossless.
 * @return {{collapsed:Set<string>, hidden:Set<string>, rep:Map<string,string>}}
 */
export const computeCollapse = (nodes) => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const collapsed = new Set(
      nodes.filter((n) => n.kind === 'group' && n.data && n.data.collapsed).map((n) => n.id),
  );
  const hidden = new Set();
  const rep = new Map();
  nodes.forEach((n) => {
    let outermost = null;
    const seen = new Set();
    let p = n.parentId ? byId.get(n.parentId) : null;
    while (p && !seen.has(p.id)) {
      seen.add(p.id);
      if (collapsed.has(p.id)) outermost = p.id; // keep climbing → highest collapsed wins
      p = p.parentId ? byId.get(p.parentId) : null;
    }
    if (outermost) {
      hidden.add(n.id);
      rep.set(n.id, outermost);
    } else {
      rep.set(n.id, n.id);
    }
  });
  return {collapsed, hidden, rep};
};

/** Depth = number of ancestors, used for the parents-first stable sort. */
const depthOf = (node, byId) => {
  let d = 0;
  let cur = node;
  const seen = new Set();
  while (cur && cur.parentId && !seen.has(cur.parentId)) {
    seen.add(cur.parentId);
    cur = byId.get(cur.parentId);
    d += 1;
  }
  return d;
};

/**
 * §B2.3 — React Flow v12 requires a parent to precede its children.
 * Stable sort by ancestor depth (parents first), preserving order within a level.
 */
export const sortNodesParentFirst = (nodes) => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return nodes
      .map((n, i) => ({n, i, d: depthOf(n, byId)}))
      .sort((a, b) => (a.d - b.d) || (a.i - b.i))
      .map((x) => x.n);
};

/** Structural signature so the ordering memo only recomputes on grouping changes (§B2.9). */
export const structuralKey = (nodes) => nodes.map((n) => `${n.id}:${n.parentId || ''}`).join('|');

/** Absolute position of a node = its position + all ancestor group positions (§B2.4). */
export const absolutePosition = (nodes, nodeId) => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  let node = byId.get(nodeId);
  let x = 0;
  let y = 0;
  const seen = new Set();
  while (node && !seen.has(node.id)) {
    seen.add(node.id);
    x += node.position.x;
    y += node.position.y;
    node = node.parentId ? byId.get(node.parentId) : null;
  }
  return {x, y};
};

/**
 * §B2.2 — split incoming React Flow node changes.
 * Returns { removeIds, passthrough }. `remove` changes are pulled out for
 * removeWithCascade; in-batch descendants are dropped so a group delete goes
 * through ungroup logic, not a blanket wipe. All non-remove changes pass through.
 */
export const partitionNodeChanges = (changes, nodes) => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const removeSet = new Set(changes.filter((c) => c.type === 'remove').map((c) => c.id));
  const hasAncestorInRemoveSet = (id) => {
    let cur = byId.get(id);
    const seen = new Set();
    while (cur && cur.parentId && !seen.has(cur.parentId)) {
      seen.add(cur.parentId);
      if (removeSet.has(cur.parentId)) return true;
      cur = byId.get(cur.parentId);
    }
    return false;
  };
  const removeIds = [...removeSet].filter((id) => !hasAncestorInRemoveSet(id));
  const passthrough = changes.filter((c) => c.type !== 'remove');
  return {removeIds, passthrough};
};

/** Find a system edge between a and b ignoring orientation (§ behavior 11 canonical pair). */
export const canonicalEdgeBetween = (edges, a, b) =>
  edges.find((e) => (e.source === a && e.target === b) || (e.source === b && e.target === a)) || null;

/** A node's ancestor ids (parent, grandparent, …), outermost last. */
export const ancestorIds = (nodes, id) => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const acc = [];
  let cur = byId.get(id);
  const seen = new Set();
  while (cur && cur.parentId && !seen.has(cur.parentId)) {
    seen.add(cur.parentId);
    acc.push(cur.parentId);
    cur = byId.get(cur.parentId);
  }
  return acc;
};

/**
 * Find a system edge that already connects a and b, treating a group edge as covering
 * its members: matches an edge between {a ∪ a's ancestor groups} and {b ∪ b's ancestors}.
 * Lets an element mapping reuse an existing group↔node edge instead of adding a redundant
 * member↔node edge in system view (behavior 11). Returns the edge or null.
 */
export const findConnectingEdge = (nodes, edges, a, b) => {
  const aSet = new Set([a, ...ancestorIds(nodes, a)]);
  const bSet = new Set([b, ...ancestorIds(nodes, b)]);
  return edges.find((e) =>
    (aSet.has(e.source) && bSet.has(e.target)) ||
    (aSet.has(e.target) && bSet.has(e.source))) || null;
};

/**
 * §B2.2 cascade delete. target is one of:
 *   {node: id} | {edge: id} | {element: {nodeId, elementId}}
 * Returns a new {nodes, edges, mappings}.
 */
export const removeWithCascade = (map, target) => {
  let {nodes, edges, mappings} = map;

  if (target.node != null) {
    const id = target.node;
    const node = nodes.find((n) => n.id === id);
    if (!node) return {nodes, edges, mappings};
    // Deleting a group ungroups its children first, then removes the empty group.
    if (node.kind === 'group') {
      const ungrouped = ungroupNode({nodes, edges, mappings}, id);
      nodes = ungrouped.nodes;
      edges = ungrouped.edges;
      mappings = ungrouped.mappings;
    }
    const removedEdgeIds = new Set(edges.filter((e) => e.source === id || e.target === id).map((e) => e.id));
    nodes = nodes.filter((n) => n.id !== id);
    edges = edges.filter((e) => !removedEdgeIds.has(e.id));
    mappings = mappings.filter((m) =>
      !removedEdgeIds.has(m.edgeId) && m.sourceNodeId !== id && m.targetNodeId !== id);
    return {nodes, edges, mappings};
  }

  if (target.edge != null) {
    const id = target.edge;
    edges = edges.filter((e) => e.id !== id);
    mappings = mappings.filter((m) => m.edgeId !== id);
    return {nodes, edges, mappings};
  }

  if (target.mapping != null) {
    mappings = mappings.filter((m) => m.id !== target.mapping);
    return {nodes, edges, mappings};
  }

  if (target.table != null) {
    const {nodeId, tableId} = target.table;
    nodes = nodes.map((n) => {
      if (n.id !== nodeId) return n;
      const data = {...n.data, tables: (n.data.tables || []).filter((t) => t.id !== tableId)};
      // Drop the removed slot's data-view layout metadata so it can't pollute future roles.
      if (data.details && data.details.dvMeta && data.details.dvMeta[tableId]) {
        const dvMeta = {...data.details.dvMeta};
        delete dvMeta[tableId];
        data.details = {...data.details, dvMeta};
      }
      return {...n, data};
    });
    mappings = mappings.filter((m) =>
      !((m.sourceNodeId === nodeId && m.sourceTableId === tableId) ||
        (m.targetNodeId === nodeId && m.targetTableId === tableId)));
    return {nodes, edges, mappings};
  }

  if (target.element != null) {
    const {nodeId, elementId, tableId = null} = target.element;
    const drop = (els) => els.filter((el) => el.id !== elementId);
    nodes = nodes.map((n) => {
      if (n.id !== nodeId) return n;
      if (tableId) {
        return {...n, data: {...n.data, tables: (n.data.tables || []).map((t) =>
          t.id === tableId ? {...t, dataElements: drop(t.dataElements || [])} : t)}};
      }
      return {...n, data: {...n.data, dataElements: drop(n.data.dataElements || [])}};
    });
    const match = (mn, mt, me) => mn === nodeId && (mt || null) === (tableId || null) && me === elementId;
    mappings = mappings.filter((m) =>
      !(match(m.sourceNodeId, m.sourceTableId, m.sourceElementId) ||
        match(m.targetNodeId, m.targetTableId, m.targetElementId)));
    return {nodes, edges, mappings};
  }
  return {nodes, edges, mappings};
};

/**
 * §B2.4 — group 2+ nodes into a new visual container.
 * memberIds must share a parent (typical selection); the group inherits it.
 * Box = members' bounding box + padding, computed ONCE here.
 */
export const groupNodes = (map, memberIds, groupId) => {
  const {nodes, edges, mappings} = map;
  const members = nodes.filter((n) => memberIds.includes(n.id));
  if (members.length < 2) return map;
  const parentId = members[0].parentId || null;
  // Only group members that share the first member's parent (keeps coords consistent).
  const grouped = members.filter((n) => (n.parentId || null) === parentId);
  const xs = grouped.map((n) => n.position.x);
  const ys = grouped.map((n) => n.position.y);
  const ws = grouped.map((n) => n.width || 172);
  const hs = grouped.map((n) => n.height || 92);
  const minX = Math.min(...xs) - GROUP_PADDING;
  const minY = Math.min(...ys) - GROUP_PADDING;
  const maxX = Math.max(...grouped.map((n, i) => xs[i] + ws[i])) + GROUP_PADDING;
  const maxY = Math.max(...grouped.map((n, i) => ys[i] + hs[i])) + GROUP_PADDING;

  const groupNode = {
    id: groupId,
    kind: 'group',
    type: 'groupNode',
    parentId,
    extent: parentId ? 'parent' : undefined,
    position: {x: minX, y: minY},
    width: maxX - minX,
    height: maxY - minY,
    data: {label: 'Group'},
  };
  const groupedIds = new Set(grouped.map((n) => n.id));
  const nextNodes = nodes.map((n) => groupedIds.has(n.id) ? {
    ...n,
    parentId: groupId,
    extent: 'parent',
    position: {x: n.position.x - minX, y: n.position.y - minY}, // relative to group
  } : n);
  // Group node must precede its children in the array (§B2.3 handled by selector,
  // but keep the raw array sane too). Spread `...map` so top-level fields
  // (id/name/settings/lockVersion/status…) survive — withMap REPLACES the map.
  return {...map, nodes: [groupNode, ...nextNodes], edges, mappings};
};

/** §B2.4 — dissolve a group: reparent children to the group's parent at correct absolute coords. */
export const ungroupNode = (map, groupId) => {
  const {nodes, edges, mappings} = map;
  const group = nodes.find((n) => n.id === groupId);
  if (!group) return map;
  const groupParent = group.parentId || null;
  const groupAbs = absolutePosition(nodes, groupId);
  const parentAbs = groupParent ? absolutePosition(nodes, groupParent) : {x: 0, y: 0};

  const nextNodes = nodes
      .filter((n) => n.id !== groupId)
      .map((n) => {
        if (n.parentId !== groupId) return n;
        // child abs = groupAbs + child.position; new relative = childAbs - parentAbs
        const childAbs = {x: groupAbs.x + n.position.x, y: groupAbs.y + n.position.y};
        return {
          ...n,
          parentId: groupParent,
          extent: groupParent ? 'parent' : undefined,
          position: {x: childAbs.x - parentAbs.x, y: childAbs.y - parentAbs.y},
        };
      });
  // A dissolved group's own edges/mappings are dropped (group no longer exists).
  const removedEdgeIds = new Set(edges.filter((e) => e.source === groupId || e.target === groupId).map((e) => e.id));
  // Spread `...map` so top-level fields survive — withMap REPLACES the map.
  return {
    ...map,
    nodes: nextNodes,
    edges: edges.filter((e) => !removedEdgeIds.has(e.id)),
    mappings: mappings.filter((m) => !removedEdgeIds.has(m.edgeId)),
  };
};

/**
 * §B2.9 — apply highlight flags to node.data, preserving object identity for any
 * node whose flags did not change so React Flow's per-node memo can skip it.
 */
export const applyHighlight = (nodes, highlightedNodeIds, highlightedElementKeys) => {
  const anyHighlight = highlightedNodeIds.size > 0 || highlightedElementKeys.size > 0;
  return nodes.map((n) => {
    const highlighted = highlightedNodeIds.has(n.id);
    const dimmed = anyHighlight && !highlighted;
    const prev = n.data || {};
    if (prev.highlighted === highlighted && prev.dimmed === dimmed &&
        prev.highlightedElementKeys === highlightedElementKeys) {
      return n; // unchanged → same ref
    }
    return {...n, data: {...prev, highlighted, dimmed, highlightedElementKeys}};
  });
};
